import React, { useState } from 'react';
import { SKY_BROADBAND_T_AND_C_URL, TERMS_AND_CONDITIONS_DOMESTIC } from '../../config';
import { links, useJSONConfig } from '@sky-tv-group/shared';
import { configService } from '../../services';
import DOMPurify from 'dompurify';

interface TermsAndConditionsProps {
  isActiveSkyStarter: boolean;
  isSsnCustomer?: boolean;
}

const TermsAndConditions = ({ isActiveSkyStarter, isSsnCustomer }: TermsAndConditionsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const config = useJSONConfig(configService);
  const mbnzSpeedDisclaimer = config?.broadband?.acquisition?.mbnzSpeedDisclaimer?.desc;
  return (
    <>
      <div className="flex flex-col justify-center items-center md:w-11/12 m-auto">
        <p
          className="sky-sub md:sky-sub w-full text-left"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(mbnzSpeedDisclaimer || ''),
          }}></p>

        {isSsnCustomer && (
          <p className="sky-sub md:sky-sub mt-4 w-full text-left">
            Sky Broadband is a fibre-based internet service, however fibre is not available at all addresses. If your
            property is eligible and does not already have a fibre connection, installation will be required as part of
            your sign-up.
          </p>
        )}
      </div>
      {!isSsnCustomer && (
        <div className="m-auto flex flex-col items-center justify-center sky-sub pt-10 pb-8">
          <div>
            <a href={TERMS_AND_CONDITIONS_DOMESTIC} className="text-primary-day-light underline">
              Consumer Terms
            </a>
            ,{' '}
            <a href={SKY_BROADBAND_T_AND_C_URL} className="text-primary-day-light underline">
              Broadband Terms
            </a>{' '}
            and{' '}
            <a href={links.bbExitFees} className="text-primary-day-light underline">
              Exit Fees
            </a>{' '}
            apply to all plans.
          </div>
        </div>
      )}

      {isSsnCustomer && (
        <div className="md:w-11/12 mx-auto pt-10" id="termAndCondition">
          {/* Accordion Header */}
          <div
            className="flex justify-between items-center sky-bg-gray-light px-6 py-2s cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}>
            <h2 className="sky-h4-bold md:sky-h3-bold">Terms and Conditions</h2>
            <span className="ml-auto h-20 w-12 flex items-center justify-center sky-h3 md:sky-h4 bg-transparent border-none cursor-pointer">
              {isOpen ? '−' : '+'}
            </span>
          </div>

          {/* Accordion Content */}
          {isOpen && (
            <div className="px-12 py-4 sky-bg-gray-lighter">
              <ul className="list-disc pl-6 space-y-1 text-lg text-black">
                <li>
                  This offer is available to Sky Sport Now customers who sign up to a standalone Sky Broadband Fibre Pro
                  or Fibre Everyday plan on a 12-month contract and redeem <strong>SSNBROADBAND</strong> voucher code at
                  sign-up. Fibre Starter plan is excluded from this offer.
                </li>

                <li>
                  To receive the promotional pricing, the voucher code must be redeemed during the checkout process by
                  11:59pm on 30 September 2026.
                </li>

                <li>
                  Eligible customers will receive the following monthly discounts on the selected Sky Broadband plan for
                  12 consecutive months from the service activation date:
                  <ul className="list-disc pl-6 mt-3 space-y-2">
                    <li>$30 per month discount on Fibre Pro</li>
                    <li>$20 per month discount on Fibre Everyday</li>
                  </ul>
                </li>

                <li>
                  The offer does not apply to one-off charges, including but not limited to installation or technician
                  fees (e.g. Super Set Up Technician).{' '}
                </li>

                <li>Existing standalone Sky Broadband customers are not eligible for this offer.</li>

                <li>
                  After the 12-month promotional period ends, your monthly subscription will automatically renew at the
                  standard monthly broadband charges in effect at the time of renewal until cancelled by you.
                </li>

                <li>
                  If you upgrade or downgrade your Sky Broadband plan during the promotional period, the discount will
                  be adjusted to the applicable plan discount.
                </li>

                <li>
                  Early termination charges may apply if you cancel your Sky Broadband service during the contract term.{' '}
                  <a
                    href="https://help.sky.co.nz/s/article/early-termination-fee"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-day-light underline">
                    See details here.
                  </a>
                </li>

                <li>We reserve the right to modify or withdraw this offer in our reasonable discretion.</li>

                <li>
                  <a
                    href="https://prelive.sky.co.nz/consumer-terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-day-light underline">
                    Sky Consumer Terms
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.sky.co.nz/broadband-terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-day-light underline">
                    Sky Broadband Terms
                  </a>{' '}
                  apply.
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export { TermsAndConditions };
